import React, { useEffect, useRef } from 'react';
import SafeIcon from '../common/SafeIcon';
import { calculateEffortCost } from '../utils/calculations';

const EffortRow = ({
  effort,
  settings,
  updateEffort,
  removeEffort,
  autoFocus = false
}) => {
  const descriptionRef = useRef(null);
  const cost = calculateEffortCost(effort, settings);

  useEffect(() => {
    if (!autoFocus || !descriptionRef.current) {
      return;
    }

    descriptionRef.current.focus();
    const cursorPosition = descriptionRef.current.value.length;
    descriptionRef.current.setSelectionRange(cursorPosition, cursorPosition);
  }, [autoFocus]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    const numericField = name === 'freq' || name === 'time';

    updateEffort(
      effort.id,
      name,
      numericField && value !== '' ? Number(value) : value
    );
  };

  const resizeDescription = (event) => {
    event.target.style.height = 'auto';
    event.target.style.height = `${event.target.scrollHeight}px`;
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[auto_minmax(220px,1fr)_auto] items-start gap-4 py-4 border-b border-gray-100 last:border-0 hover:bg-gray-50 px-3 rounded-lg transition-colors print:p-0 print:border-gray-300">
      <button
        onClick={() => removeEffort(effort.id)}
        className="justify-self-start p-2 text-gray-400 hover:text-red-500 transition-colors no-print"
        aria-label="Aufwandsschritt entfernen"
        title="Aufwandsschritt entfernen"
      >
        <SafeIcon name="Trash2" className="text-lg" />
      </button>

      <div className="min-w-0 w-full">
        <textarea
          ref={descriptionRef}
          name="name"
          value={effort.name}
          onChange={handleChange}
          onInput={resizeDescription}
          placeholder="Tätigkeit beschreiben..."
          rows={3}
          className="w-full min-h-[6.5rem] min-w-0 bg-transparent border border-gray-200 rounded-md hover:border-gray-300 focus:border-red-500 focus:ring-1 focus:ring-red-500 px-3 py-2 text-sm font-medium text-gray-700 resize-y overflow-y-auto whitespace-normal break-words"
          aria-label="Tätigkeitsbeschreibung"
        />
      </div>

      <div className="w-full xl:w-[350px]">
        <div className="flex flex-wrap xl:flex-nowrap items-center gap-2 pt-2">
          <input
            type="number"
            name="freq"
            value={effort.freq}
            onChange={handleChange}
            className="w-16 shrink-0 px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-red-500"
            aria-label="Häufigkeit"
          />

          <span className="text-sm text-gray-500 whitespace-nowrap">
            x pro
          </span>

          <select
            name="freqUnit"
            value={effort.freqUnit}
            onChange={handleChange}
            className="shrink-0 px-2 py-1 border border-gray-300 rounded-md text-sm bg-white focus:ring-red-500"
            aria-label="Zeiteinheit der Häufigkeit"
          >
            <option value="day">Tag</option>
            <option value="week">Woche</option>
            <option value="month">Monat</option>
            <option value="year">Jahr</option>
          </select>

          <span className="text-gray-400 whitespace-nowrap">à</span>

          <input
            type="number"
            name="time"
            value={effort.time}
            onChange={handleChange}
            className="w-16 shrink-0 px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-red-500"
            aria-label="Zeit"
          />

          <select
            name="timeUnit"
            value={effort.timeUnit}
            onChange={handleChange}
            className="shrink-0 px-2 py-1 border border-gray-300 rounded-md text-sm bg-white focus:ring-red-500"
            aria-label="Zeiteinheit"
          >
            <option value="min">Min.</option>
            <option value="hr">Std.</option>
          </select>
        </div>

        <div className="mt-4 text-right font-bold text-red-600 whitespace-nowrap">
          {cost.toLocaleString('de-DE', {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: 0
          })}{' '}
          / J
        </div>
      </div>
    </div>
  );
};

export default EffortRow;